import { ConnectedPosition, Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'
import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core'
import { OneTooltipComponent } from '../components/one-tooltip/one-tooltip.component'

@Directive({
    selector: '[oneTooltip]',
    standalone: true
})
export class OneTooltipDirective implements OnInit, OnDestroy {
  @Input('oneTooltip') content: string | TemplateRef<any> | { content: TemplateRef<any>, context: any }
  @Input('oneTooltipTransparent') transparent: boolean
  @Input('oneTooltipPosition') position: 'top' | 'bottom'
  @Input('oneTooltipSkipUnderline') skipUnderline: boolean
  @Input('oneTooltipMaxWidth') maxWidth: number
  @Input('oneTooltipWidth') width: number
  @Input('oneTooltipInteractive') interactive = false

  private overlayRef: OverlayRef
  private closeTimeout: any
  private isTooltipMouseOver = false

  constructor(
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private overlay: Overlay
  ) { }

  ngOnInit() {
    if (this.content) {
      this.ensureOverlayRef()
    }
  }

  /**
   * Content may be empty on first init (e.g. Transloco/async) and set later — overlay must be created lazily.
   */
  private ensureOverlayRef() {
    if (this.overlayRef) {
      return
    }
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([this.topPosition])
    this.overlayRef = this.overlay.create({ positionStrategy })
    this.elementRef.nativeElement.style.cursor = 'pointer'
    if (!this.skipUnderline) {
      this.elementRef.nativeElement.style.borderBottom = '2px dotted #c4cdd5'
    }
  }

  @HostListener('mouseenter') show() {
    if (!this.content) return

    this.ensureOverlayRef()
    clearTimeout(this.closeTimeout)
    if (this.overlayRef.hasAttached()) {
      return
    }

    const tooltipRef = this.overlayRef.attach(new ComponentPortal(OneTooltipComponent))
    tooltipRef.instance.transparent = this.transparent
    tooltipRef.instance.interactive = this.interactive
    
    if (typeof this.content === 'string') {
      tooltipRef.instance.text = this.content
    } else if (this.content instanceof TemplateRef) {
      tooltipRef.instance.tpl = this.content
    } else if (typeof this.content === 'object' && this.content.content instanceof TemplateRef) {
      tooltipRef.instance.tpl = this.content.content
      tooltipRef.instance.context = this.content.context
    }
    
    if (this.maxWidth) {
      tooltipRef.instance.maxWidth = this.maxWidth
    }
    if (this.width) {
      tooltipRef.instance.width = this.width
    }
    tooltipRef.location.nativeElement.style.opacity = 0 // don't display tooltip until position is set below
    if (this.transparent) {
      tooltipRef.location.nativeElement.style.filter = 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2))'
    }
    setTimeout(() => {
      const viewTopPos = this.elementRef.nativeElement.getBoundingClientRect()[this.position || 'top']
      let positions
      if (this.position === 'top') {
        positions = this.topPosition
      } else if (this.position === 'bottom') {
        positions = this.bottomPosition
      } else {
        positions = this.overlayRef.overlayElement.offsetHeight >= viewTopPos ? this.bottomPosition : this.topPosition
      }
      const newPositionStrategy = this.overlay.position()
        .flexibleConnectedTo(this.elementRef)
        .withPositions([positions])
      this.overlayRef.updatePositionStrategy(newPositionStrategy)
      tooltipRef.location.nativeElement.style.opacity = 1
    })

    if (this.interactive) {
      this.addTooltipMouseListeners()
    }
  }

  @HostListener('mouseleave') hide() {
    if (!this.content) return

    if (this.interactive) {
      this.startCloseTimeout()
    } else {
      this.close()
    }
  }

  private addTooltipMouseListeners() {
    this.overlayRef.overlayElement.addEventListener('mouseenter', () => {
      this.isTooltipMouseOver = true
      clearTimeout(this.closeTimeout)
    })

    this.overlayRef.overlayElement.addEventListener('mouseleave', () => {
      this.isTooltipMouseOver = false
      this.startCloseTimeout()
    })
  }

  private startCloseTimeout() {
    this.closeTimeout = setTimeout(() => {
      if (!this.isTooltipMouseOver) {
        this.close()
      }
    }, 100) // Adjust this delay as needed
  }

  @HostListener('window:scroll') onScroll() {
    this.close()
  }

  get topPosition(): ConnectedPosition {
    return {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
    }
  }

  get bottomPosition(): ConnectedPosition {
    return {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
    }
  }

  close() {
    clearTimeout(this.closeTimeout)
    this.overlayRef?.detach()
  }

  ngOnDestroy() {
    this.close()
  }
}
